import React from 'react';
import checkedImg from '../../assets/checked.png';
import { useTranslation } from 'react-i18next';

// Add custom styles for animations
const styles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes bounceIn {
    0% { opacity: 0; transform: scale(0.3); }
    50% { opacity: 1; transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
  .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
  .animate-bounceIn { animation: bounceIn 0.8s ease-out; }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const SuccessRegistration = () => {
    const { t } = useTranslation();

    return (
        <div className="flex justify-center items-center bg-gradient-to-br from-green-50 to-blue-50 animate-fadeIn">
            <div className="max-w-lg w-full bg-white/95 backdrop-blur-xl shadow-2xl rounded-[2rem] overflow-hidden transform animate-bounceIn border border-white/100">
                {/* Success Header with gradient */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-white/10 transform -skew-y-2"></div>
                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg transform animate-pulse">
                            <svg className="w-12 h-12 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-2">
                            🎉 {t('registration_successful')}
                        </h2>
                        <p className="text-green-100 text-lg">{t('success_product_registered')}</p>
                    </div>
                </div>

                <div className="p-8 text-center">
                    {/* Enhanced Success Icon */}
                    <div className="relative mb-8">
                        <div className="absolute inset-0 bg-green-200 rounded-full animate-ping opacity-20"></div>
                        <img 
                            src={checkedImg} 
                            alt="Success" 
                            className="relative mx-auto h-32 w-32 transform hover:scale-110 transition-transform duration-300"
                        />
                    </div>

                    {/* Warranty Confirmation Message */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8 border border-green-200">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-3">
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">{t('success_warranty_confirmed')}</h3>
                        </div>
                        <div className="text-center mb-4">
                            <p className="text-gray-700 text-lg leading-relaxed mb-4">
                                <strong>{t('success_congratulations')}</strong> {t('success_warranty_message')} <strong className="text-green-600">{t('success_2_months')}</strong>
                            </p>
                            <div className="bg-white rounded-xl p-4 border border-green-300 shadow-sm">
                                <div className="flex items-center justify-center text-green-700">
                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold">{t('success_warranty_extension')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Success Message */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-3">{t('success_whats_next')}</h3>
                        <p className="text-gray-600 text-lg mb-4">
                            {t('download_app_to_track_warranty')}
                        </p>
                        <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 mb-4">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {t('success_warranty_activated')}
                            </div>
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {t('success_priority_support')}
                            </div>
                        </div>
                    </div>

                    {/* Enhanced App Download Buttons */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-gray-800 mb-4">{t('success_download_app')}</h4>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <a 
                                href="https://play.google.com/store/apps/details?id=com.prodyt.app" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="group flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-2xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-300/50"
                            >
                                <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                </svg>
                                <div className="text-left">
                                    <div className="text-xs opacity-90">{t('success_get_it_on')}</div>
                                    <div className="text-sm font-bold">{t('success_google_play')}</div>
                                </div>
                                <svg className="w-5 h-5 ml-3 transform group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </a>
                            <a 
                                href="https://apps.apple.com/in/app/prodyt/id6742870082" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="group flex items-center bg-gradient-to-r from-gray-800 to-black text-white px-6 py-3 rounded-2xl hover:from-gray-900 hover:to-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-gray-400/30"
                            >
                                <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                                </svg>
                                <div className="text-left">
                                    <div className="text-xs opacity-90">{t('success_download_on')}</div>
                                    <div className="text-sm font-bold">{t('success_app_store')}</div>
                                </div>
                                <svg className="w-5 h-5 ml-3 transform group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </a>
                        </div>
                        
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuccessRegistration;
